import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Button, Chip, Divider, CircularProgress,
  Alert, useTheme, alpha, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Edit as EditIcon,
  Inventory as ItemIcon,
  ReceiptLong as InvoiceIcon,
  ShoppingCart as POIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  AccountBalance as ConsignmentIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import supplierService from '../../../services/supplierService';
import purchaseOrderService from '../../../services/purchaseOrderService';
import invoiceService from '../../../services/invoiceService';
import venueService from '../../../services/venueService';
import POStatusBadge from '../../../components/venue/POStatusBadge';
import InvoiceDetailPanel from '../../../components/venue/InvoiceDetailPanel';
import type { Supplier, PurchaseOrder, Invoice } from '../../../types/venue';
import type { SupplierItemLink } from '../../../types/supplierItemLink';
import { fromMinorUnits } from '../../../utils/money';

const OPEN_STATUSES = ['draft', 'ordered', 'partially_received'];

const INVOICE_STATUS_STYLE: Record<string, { bg: string; color: string }> = {
  draft: { bg: '#f3f4f6', color: '#374151' },
  open:  { bg: '#dafbe1', color: '#1a7f37' },
  void:  { bg: '#fde8e8', color: '#b91c1c' },
};

const LabelValue: React.FC<{ label: string; value?: string }> = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ mt: 0.25 }}>{value || '—'}</Typography>
  </Box>
);

const formatDate = (iso?: string | null) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};


const SupplierDetail: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { supplierId } = useParams<{ supplierId: string }>();

  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState('');

  // Open purchase orders for this supplier
  const [openPOs, setOpenPOs] = useState<PurchaseOrder[]>([]);
  const [posLoading, setPosLoading] = useState(false);

  // Linked inventory items
  const [itemLinks, setItemLinks] = useState<SupplierItemLink[]>([]);
  const [itemLinksLoading, setItemLinksLoading] = useState(false);

  // Invoices for this supplier
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [viewingInvoice, setViewingInvoice] = useState<Invoice | null>(null);

  // Venue names for venues that have open POs with this supplier
  const [venueNamesMap, setVenueNamesMap] = useState<Map<string, string>>(new Map());

  const fetchSupplier = useCallback(async () => {
    if (!supplierId) return;
    setLoading(true);
    try {
      const data = await supplierService.getSupplier(supplierId);
      setSupplier(data);
    } catch {
      setApiError('Failed to load supplier.');
    } finally {
      setLoading(false);
    }
  }, [supplierId]);

  const fetchOpenPOs = useCallback(async () => {
    if (!supplierId) return;
    setPosLoading(true);
    try {
      // Load all three open statuses in parallel, no venueId filter (cross-venue view)
      const results = await Promise.all(
        OPEN_STATUSES.map(s =>
          purchaseOrderService.getOrders(undefined, 1, 50, s as any, supplierId)
            .then(r => r.data ?? [])
            .catch(() => [] as PurchaseOrder[])
        )
      );
      const all = results.flat().sort((a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
      setOpenPOs(all);

      // Fetch venue names for unique venue IDs in open POs
      const uniqueVenueIds = Array.from(new Set(all.map(po => po.venueId).filter(Boolean)));
      if (uniqueVenueIds.length > 0) {
        const venueResults = await Promise.allSettled(
          uniqueVenueIds.map(id => venueService.getVenue(id))
        );
        const map = new Map<string, string>();
        venueResults.forEach((result, i) => {
          if (result.status === 'fulfilled') map.set(uniqueVenueIds[i], result.value.name);
        });
        setVenueNamesMap(map);
      }
    } finally {
      setPosLoading(false);
    }
  }, [supplierId]);

  const fetchItemLinks = useCallback(async () => {
    if (!supplierId) return;
    setItemLinksLoading(true);
    try {
      const data = await supplierService.getItemLinks(supplierId);
      setItemLinks(data);
    } catch {
      /* non-blocking */
    } finally {
      setItemLinksLoading(false);
    }
  }, [supplierId]);

  const fetchInvoices = useCallback(async () => {
    if (!supplierId) return;
    setInvoicesLoading(true);
    try {
      const result = await invoiceService.getInvoicesBySupplier(supplierId);
      setInvoices(result.data ?? []);
    } catch {
      /* non-blocking — section will show empty state */
    } finally {
      setInvoicesLoading(false);
    }
  }, [supplierId]);

  useEffect(() => { fetchSupplier(); }, [fetchSupplier]);
  useEffect(() => { fetchOpenPOs(); }, [fetchOpenPOs]);
  useEffect(() => { fetchItemLinks(); }, [fetchItemLinks]);
  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const handleDeactivate = async () => {
    if (!supplier) return;
    if (!window.confirm(`Deactivate "${supplier.name}"?`)) return;
    try {
      await supplierService.deleteSupplier(supplier._id);
      navigate('/admin/suppliers');
    } catch {
      setApiError('Failed to deactivate supplier.');
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (apiError || !supplier) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{apiError || 'Supplier not found.'}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* Top bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/admin/suppliers')} sx={{ textTransform: 'none', color: theme.palette.text.secondary }}>
          Suppliers
        </Button>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<DeleteIcon />}
            color="error"
            onClick={handleDeactivate}
            sx={{ textTransform: 'none' }}
          >
            Deactivate
          </Button>
          <Button
            startIcon={<EditIcon />}
            variant="outlined"
            onClick={() => navigate(`/admin/suppliers/edit/${supplierId}`)}
            sx={{ textTransform: 'none' }}
          >
            Edit
          </Button>
        </Box>
      </Box>

      {/* Supplier header */}
      <Paper sx={{ p: 2.5, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="h5" fontWeight={700}>{supplier.name}</Typography>
            {supplier.shortName && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{supplier.shortName}</Typography>
            )}
          </Box>
          <Chip
            label={supplier.active ? 'Active' : 'Inactive'}
            color={supplier.active ? 'success' : 'default'}
            size="small"
            sx={{ mt: 0.5 }}
          />
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <LabelValue label="Type" value={supplier.type.replace('_', ' ')} />
          <LabelValue label="Country" value={supplier.country} />
          <LabelValue label="Account Ref" value={supplier.accountReference} />
        </Box>

        {(supplier.contactName || supplier.contactEmail || supplier.contactPhone || supplier.website) && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <LabelValue label="Contact" value={supplier.contactName} />
              <LabelValue label="Email" value={supplier.contactEmail} />
              <LabelValue label="Phone" value={supplier.contactPhone} />
              <LabelValue label="Website" value={supplier.website} />
            </Box>
          </>
        )}

        {supplier.notes && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Notes
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>{supplier.notes}</Typography>
            </Box>
          </>
        )}
      </Paper>

      <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap' }}>
        {/* Linked Inventory Items */}
        <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', flex: '1 1 340px' }}>
          <Box sx={{
            px: 3, py: 2,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ItemIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
              <Typography variant="subtitle1" fontWeight={600}>Linked Items</Typography>
              {!itemLinksLoading && itemLinks.length > 0 && (
                <Typography variant="caption" color="text.secondary">({itemLinks.length})</Typography>
              )}
            </Box>
          </Box>

          {itemLinksLoading ? (
            <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          ) : itemLinks.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center', color: theme.palette.text.secondary }}>
              <Typography variant="body2">No items received from this supplier yet.</Typography>
              <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                Items are linked automatically when a delivery is posted.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Item', 'Size', 'Last Cost', 'Last Received'].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 600, color: theme.palette.text.secondary, fontSize: '0.7rem', textTransform: 'uppercase', py: 1.25 }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {itemLinks.map(link => (
                    <TableRow key={link._id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {link.isPreferred && (
                            <StarIcon sx={{ fontSize: 13, color: '#9a6700' }} />
                          )}
                          <Box>
                            <Typography variant="body2" fontWeight={500}>
                              {link.displayName ?? link.inventoryItemId}
                            </Typography>
                            {link.supplierReference && (
                              <Typography variant="caption" color="text.secondary">
                                Ref: {link.supplierReference}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {link.sizeValue != null && link.sizeUnit ? `${link.sizeValue} ${link.sizeUnit}` : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {link.lastReceivedCost != null ? fromMinorUnits(link.lastReceivedCost).toFixed(2) : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(link.lastReceivedDate)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        {/* Open Purchase Orders */}
        <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', flex: '1 1 340px' }}>
          <Box sx={{
            px: 3, py: 2,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <POIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
              <Typography variant="subtitle1" fontWeight={600}>Open Purchase Orders</Typography>
              {!posLoading && openPOs.length > 0 && (
                <Typography variant="caption" color="text.secondary">({openPOs.length})</Typography>
              )}
            </Box>
          </Box>

          {posLoading ? (
            <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          ) : openPOs.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center', color: theme.palette.text.secondary }}>
              <Typography variant="body2">No open purchase orders.</Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Order ID', 'Status', 'Date', 'Expected'].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 600, color: theme.palette.text.secondary, fontSize: '0.7rem', textTransform: 'uppercase', py: 1.25 }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {openPOs.map(po => (
                    <TableRow
                      key={po._id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/admin/venues/${po.venueId}/purchasing/${po._id}`)}
                    >
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace" fontSize="0.78rem" fontWeight={600}>
                          {po._id.slice(-8).toUpperCase()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <POStatusBadge status={po.status} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{formatDate(po.orderedAt ?? po.createdAt)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{formatDate(po.expectedDeliveryDate)}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>

      {/* Invoices */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Box sx={{
          px: 3, py: 2,
          display: 'flex', alignItems: 'center', gap: 1,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        }}>
          <InvoiceIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
          <Typography variant="subtitle1" fontWeight={600}>Invoices</Typography>
          {!invoicesLoading && invoices.length > 0 && (
            <Typography variant="caption" color="text.secondary">({invoices.length})</Typography>
          )}
        </Box>

        {invoicesLoading ? (
          <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : invoices.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center', color: theme.palette.text.secondary }}>
            <Typography variant="body2">No invoices for this supplier.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Invoice #', 'Status', 'Invoice Date', 'Total Amount', 'PO Reference'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 600, color: theme.palette.text.secondary, fontSize: '0.7rem', textTransform: 'uppercase', py: 1.25 }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.map(inv => {
                  const style = INVOICE_STATUS_STYLE[inv.status] ?? INVOICE_STATUS_STYLE.draft;
                  return (
                    <TableRow
                      key={inv._id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => setViewingInvoice(inv)}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} fontFamily="monospace" fontSize="0.82rem">
                          {inv.invoiceNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-block',
                            px: 1, py: 0.25,
                            borderRadius: 1,
                            fontSize: '0.72rem',
                            fontWeight: 600,
                            textTransform: 'capitalize',
                            backgroundColor: style.bg,
                            color: style.color,
                          }}
                        >
                          {inv.status}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">{formatDate(inv.invoiceDate)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {inv.totalAmount != null
                            ? `${inv.currencyCode ? inv.currencyCode + ' ' : ''}${inv.totalAmount.toFixed(2)}`
                            : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {inv.purchaseOrderId ? (
                          <Typography variant="body2" fontFamily="monospace" fontSize="0.78rem" color="text.secondary">
                            {inv.purchaseOrderId.slice(-8).toUpperCase()}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Consignment Accounts */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Box sx={{
          px: 3, py: 2,
          display: 'flex', alignItems: 'center', gap: 1,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        }}>
          <ConsignmentIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
          <Typography variant="subtitle1" fontWeight={600}>Consignment</Typography>
        </Box>

        {posLoading ? (
          <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : venueNamesMap.size === 0 ? (
          <Box sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No active purchase orders found from this supplier. Consignment accounts are configured per venue — open a venue's Ownership section to create a consignment account linked to this supplier.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Venue', 'Actions'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 600, color: theme.palette.text.secondary, fontSize: '0.7rem', textTransform: 'uppercase', py: 1.25 }}>
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.from(venueNamesMap.entries()).map(([venueId, venueName]) => (
                  <TableRow key={venueId}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{venueName}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => navigate(`/admin/venues/${venueId}/ownership-accounts/create?type=CONSIGNED&supplierId=${supplierId}`)}
                          sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                        >
                          Create consignment account
                        </Button>
                        <Button
                          size="small"
                          onClick={() => navigate(`/admin/venues/${venueId}/ownership-reports`)}
                          sx={{ textTransform: 'none', fontSize: '0.75rem', color: 'text.secondary' }}
                        >
                          View consignment report →
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Invoice detail panel */}
      {viewingInvoice && (
        <InvoiceDetailPanel
          open
          onClose={() => setViewingInvoice(null)}
          onSuccess={() => { fetchInvoices(); setViewingInvoice(null); }}
          invoice={viewingInvoice}
          venueId={viewingInvoice.venueId}
          supplierName={supplier?.name}
        />
      )}
    </Box>
  );
};

export default SupplierDetail;
